import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Search, AlertOctagon, Pill, Stethoscope } from "lucide-react";
import { ElementDetailDialog } from "../components/ElementDetailDialog";
import { CATEGORY_BG, HAZARD_BG, HAZARD_LABELS } from "../lib/categories";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SUGGESTIONS = [
  "garlic breath",
  "Prussian blue",
  "tremor",
  "pulmonary edema",
  "hair loss",
  "neuropathy",
  "cancer",
  "Parkinson",
  "chelation",
  "calcium gluconate",
];

const FIELD_BADGES = {
  symptoms:     { label: "Symptom",       cls: "bg-[#FFD700] text-zinc-950" },
  complications:{ label: "Complication",  cls: "bg-[#FF8A00] text-zinc-950" },
  firstaid:     { label: "First Aid",     cls: "bg-[#FF003C] text-white" },
  antidote:     { label: "Antidote",      cls: "bg-[#00E5FF] text-zinc-950" },
};

export default function SymptomSearch() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);

  const runSearch = useCallback(async (q) => {
    const trimmed = q.trim();
    if (trimmed.length < 2) { setResults([]); return; }
    setLoading(true);
    try {
      const r = await axios.get(`${API}/symptom-search`, { params: { q: trimmed } });
      setResults(r.data);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => runSearch(query), 300);
    return () => clearTimeout(t);
  }, [query, runSearch]);

  return (
    <div className="bg-white min-h-screen" data-testid="symptoms-page">
      <section className="border-b-2 border-zinc-950">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-600 mb-3 flex items-center gap-3">
            <span className="w-8 h-px bg-zinc-950" /> Differential · Symptom & Antidote Search
          </div>
          <h1 className="font-heading font-black text-5xl md:text-6xl lg:text-7xl leading-[0.85] tracking-tighter uppercase">
            What caused<br />
            <span className="bg-[#00E5FF] px-2 inline-block">this exposure?</span>
          </h1>
          <p className="mt-6 text-lg text-zinc-700 max-w-2xl">
            Type a symptom, antidote or treatment — we scan all 118 elements' symptoms, complications, first-aid
            and antidote text to find candidates.
          </p>
        </div>
      </section>

      {/* Search */}
      <section className="border-b-2 border-zinc-950 bg-zinc-50">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-2 border-2 border-zinc-950 bg-white px-4 py-3 max-w-2xl neo-shadow">
            <Search size={18} strokeWidth={2.5} />
            <input
              data-testid="symptom-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="e.g. 'garlic breath', 'tremor', 'chelation', 'Prussian blue'…"
              className="w-full bg-transparent border-0 outline-none font-body text-base placeholder:text-zinc-500"
              autoFocus
            />
            {query && (
              <button
                data-testid="symptom-clear"
                onClick={() => setQuery("")}
                className="font-mono text-[10px] uppercase tracking-widest underline"
              >Clear</button>
            )}
          </div>

          <div className="mt-3 flex flex-wrap gap-2 items-center">
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-600">Try:</span>
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                data-testid={`symptom-suggestion-${s}`}
                onClick={() => setQuery(s)}
                className="bg-white border-2 border-zinc-950 px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest hover:bg-[#FFD700] transition-colors"
              >{s}</button>
            ))}
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {query.trim().length < 2 ? (
          <div className="border-2 border-dashed border-zinc-300 p-10 text-center" data-testid="symptom-empty">
            <Stethoscope size={40} strokeWidth={2} className="mx-auto mb-3 text-zinc-400" />
            <div className="font-mono text-sm text-zinc-600 uppercase tracking-widest">Start typing to search</div>
          </div>
        ) : loading ? (
          <div className="font-mono text-sm text-zinc-600 text-center py-10">Searching…</div>
        ) : results.length === 0 ? (
          <div className="border-2 border-zinc-950 bg-[#FFD700] p-6 text-center" data-testid="symptom-no-results">
            <div className="font-heading font-black text-2xl uppercase">No matches for &ldquo;{query}&rdquo;</div>
            <div className="font-mono text-xs mt-2 uppercase tracking-widest">Try a simpler term (e.g. 'tremor' instead of 'tremors').</div>
          </div>
        ) : (
          <>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-600 mb-4" data-testid="symptom-count">
              {results.length} match{results.length === 1 ? "" : "es"} for &ldquo;{query}&rdquo;
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {results.map((r) => (
                <ResultCard key={r.n} result={r} query={query}
                  onOpen={() => { setSelected({ n: r.n, s: r.s, name: r.name, cat: r.cat, hazard: r.hazard }); setOpen(true); }}
                />
              ))}
            </div>
          </>
        )}
      </section>

      <ElementDetailDialog element={selected} open={open} onOpenChange={setOpen} />
    </div>
  );
}

const ResultCard = ({ result, query, onOpen }) => {
  const colorCls = CATEGORY_BG[result.cat] || "bg-zinc-200 text-black";

  // Pick the most clinically-relevant matched field's content to preview
  const previewField = result.matched.includes("antidote") ? "antidote"
    : result.matched.includes("firstaid") ? "firstaid"
    : result.matched.includes("symptoms") ? "symptoms"
    : "complications";
  const previewText = previewField === "antidote" ? result.antidote
    : previewField === "firstaid" ? result.firstaid
    : result.symptoms;

  return (
    <button
      data-testid={`symptom-result-${result.s}`}
      onClick={onOpen}
      className="text-left border-2 border-zinc-950 bg-white neo-shadow hover:neo-shadow-lg hover:-translate-y-1 transition-all duration-200 flex flex-col"
    >
      {/* Header */}
      <div className="grid grid-cols-12 border-b-2 border-zinc-950">
        <div className={`col-span-4 ${colorCls} p-3 flex flex-col items-center justify-center border-r-2 border-zinc-950`}>
          <div className="font-mono text-[9px] uppercase tracking-[0.2em] opacity-80">№ {result.n}</div>
          <div className="font-heading font-black text-4xl leading-none tracking-tighter">{result.s}</div>
        </div>
        <div className="col-span-8 p-3 flex flex-col justify-between gap-2">
          <div>
            <div className="font-heading font-bold text-lg uppercase tracking-tight leading-none">{result.name}</div>
            <div className={`${HAZARD_BG[result.hazard]} border-2 border-zinc-950 inline-block px-2 py-0.5 mt-1.5 font-mono text-[9px] uppercase tracking-widest font-bold`}>
              {HAZARD_LABELS[result.hazard]}
            </div>
          </div>
          <div className="flex flex-wrap gap-1">
            {result.matched.map((f) => (
              <span key={f} className={`${FIELD_BADGES[f].cls} border border-zinc-950 px-1.5 py-0.5 font-mono text-[8px] uppercase tracking-widest`}>
                {FIELD_BADGES[f].label}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Preview text */}
      <div className="p-3 flex-1">
        <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-600 mb-1">
          {previewField === "antidote" ? "Recommended antidote" : previewField}
        </div>
        <p className="text-sm text-zinc-900 leading-snug line-clamp-4">
          <Highlight text={previewText} query={query} />
        </p>
      </div>

      {/* CTA */}
      <div className="border-t-2 border-zinc-950 bg-zinc-50 px-3 py-2 font-mono text-[10px] uppercase tracking-widest flex items-center justify-between">
        <span className="inline-flex items-center gap-1.5 text-zinc-950">
          {previewField === "antidote" ? <Pill size={12} strokeWidth={2.75} /> : <AlertOctagon size={12} strokeWidth={2.75} />}
          {previewField === "antidote" ? "Antidote" : "Toxicology"}
        </span>
        <span className="underline">Full hazard sheet →</span>
      </div>
    </button>
  );
};

const Highlight = ({ text, query }) => {
  if (!query) return text;
  const q = query.trim();
  if (q.length < 2) return text;
  const parts = text.split(new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "ig"));
  return parts.map((p, i) =>
    p.toLowerCase() === q.toLowerCase()
      ? <mark key={i} className="bg-[#FFD700] px-0.5">{p}</mark>
      : <span key={i}>{p}</span>
  );
};
